import { Either, Fail, Success } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Рекламный канал.
 * @param {number} id   Идентификатор.
 * @param {string} name Название.
 * @constructor
 */
export function AdvertisingChannel(id, name) {
  this.id = id;
  this.name = name;
  this.errors = [];
}

/**
 * @description Шлюз рекламных каналов.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function AdvertisingChannelGateway(axios) {
  /**
   * @description Создать рекламный канал.
   * @param {string} name Название.
   * @returns {Promise<Validation>}
   */
  this.create = (name) => axios
    .post('/api/advertising-channels', { name })
    .then(
      async (response) => {
        const { message } = response.data;
        const id = response.data.advertising_channel_id;
        return Success({ message, id });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 403) {
          return Fail(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Удалить рекламный канал.
   * @param {number} id Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.delete = (id) => axios
    .delete(`/api/advertising-channels/${id}`)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 403) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 404) {
          return Fail(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Редактировать рекламный канал.
   * @param {AdvertisingChannel} item Рекламный канал.
   * @returns {Promise<Validation>}
   */
  this.edit = (item) => axios
    .put(`/api/advertising-channels/${item.id}`, { name: item.name })
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 403) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 404) {
          return Fail(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить список рекламных каналов для просмотра.
   * @returns {Promise<Validation>}
   */
  this.viewList = () => {
    const makeAdvertisingChannel = (item) => new AdvertisingChannel(item.id, item.name);
    return axios
      .get('/api/advertising-channels')
      .then(
        async (response) => {
          const list = (response.data.data || []).map(makeAdvertisingChannel);
          return Success({
            actions: response.data.actions,
            list,
            isRequired: response.data.is_required,
          });
        },
        async (error) => {
          if (error.isAxiosError) {
            if (error.response && error.response.status === 403) {
              return Fail(error.response.data);
            }
            if (!error.response) {
              throw new Error('Network error: сервер по адресу /api/advertising-channels не ответил на запрос');
            }
          }
          throw error;
        },
      );
  };
  /**
   * @description Обновить расходы на рекламные каналы.
   * @param {Array} channels Каналы.
   * @returns {Promise<Either>}
   */
  this.updateExpenditures = (channels) => axios
    .put('/api/advertising-channels-expenditures', { advertising_channels: channels })
    .then(
      async (response) => Either.right(response.data.title),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Fail(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить расходы на рекламные каналы за период.
   * @param {string} dateFrom Дата от.
   * @param {string} dateTo Дата до.
   * @returns {Promise<Either>}
   */
  this.viewExpendituresByPeriod = (dateFrom, dateTo) => axios
    .get(`/api/advertising-channels-expenditures/${dateFrom}:${dateTo}`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Fail(leftResponse);
        }
        throw error;
      },
    );
  this.changePatientSourceRequiredFlag = (isPatientSourceRequired) => axios
    .put('/api/change-patient-source-status', { isPatientSourceRequired })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
}
